import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getPaymentStatus } from '../services/skipcash';
import { useCart } from '../context/CartContext';
import './PaymentStatus.css';

function PaymentStatus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed', 'pending'
  const [message, setMessage] = useState('Verifying payment status...');
  const [orderId, setOrderId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [orderDocId, setOrderDocId] = useState(null);
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      // Prevent multiple simultaneous verifications
      if (isVerifyingRef.current) return;
      isVerifyingRef.current = true;

      // Get orderId from URL params or search params
      let orderIdToUse = searchParams.get('orderId') || searchParams.get('transactionId');
      
      // Get payment info from sessionStorage or localStorage
      let paymentIdToCheck = null;
      let orderDocIdToUse = null;
      
      // Check sessionStorage first
      const pendingOrder = sessionStorage.getItem('pendingOrder');
      if (pendingOrder) {
        try {
          const pending = JSON.parse(pendingOrder);
          if (!orderIdToUse) orderIdToUse = pending.orderId;
          paymentIdToCheck = pending.paymentId;
          orderDocIdToUse = pending.orderDocId;
        } catch (e) {
          console.error('Error parsing pending order:', e);
        }
      }
      
      // Fallback to localStorage
      if (!paymentIdToCheck || !orderDocIdToUse) {
        const keys = Object.keys(localStorage).filter(key => key.startsWith('order_'));
        if (keys.length > 0) {
          const recentOrders = keys.map(key => {
            try {
              return JSON.parse(localStorage.getItem(key));
            } catch (e) {
              return null;
            }
          }).filter(Boolean).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          
          if (recentOrders.length > 0) {
            if (!orderIdToUse) orderIdToUse = recentOrders[0].orderId;
            if (!paymentIdToCheck) paymentIdToCheck = recentOrders[0].paymentId;
            if (!orderDocIdToUse) orderDocIdToUse = recentOrders[0].orderDocId;
          }
        }
      }

      if (!orderIdToUse || !paymentIdToCheck || !orderDocIdToUse) {
        setStatus('failed');
        setMessage('Payment information not found. Please contact support.');
        isVerifyingRef.current = false;
        return;
      }

      setOrderId(orderIdToUse);
      setPaymentId(paymentIdToCheck);
      setOrderDocId(orderDocIdToUse);

      try {
        setStatus('checking');
        setMessage('Verifying payment status...');

        // Wait a moment for webhook to process (if payment just completed)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Check payment status from SkipCash API
        const statusResponse = await getPaymentStatus(paymentIdToCheck);
        
        if (statusResponse.resultObj) {
          const statusId = statusResponse.resultObj.statusId;
          console.log('SkipCash payment status:', statusId, statusResponse.resultObj.status);
          
          // Status IDs: 0=new, 1=pending, 2=paid, 3=canceled, 4=failed, 5=rejected, 6=refunded
          if (statusId === 2) {
            // Payment is paid - update order and redirect to receipt
            console.log('Payment confirmed as paid, updating order...');
            
            const orderRef = doc(db, 'orders', orderDocIdToUse);
            await updateDoc(orderRef, {
              paymentStatus: 'paid',
              status: 'confirmed',
              paymentId: paymentIdToCheck,
              updatedAt: serverTimestamp(),
            });
            
            setStatus('success');
            setMessage('Payment verified successfully!');
            
            // Clear cart
            clearCart();
            sessionStorage.removeItem('pendingOrder');
            
            // Redirect to receipt after short delay
            setTimeout(() => {
              navigate(`/receipt/${orderIdToUse}`, { replace: true });
            }, 1500);
            
            isVerifyingRef.current = false;
            
          } else if (statusId === 3) {
            // Payment canceled
            setStatus('failed');
            setMessage('Payment was canceled');
            
            const orderRef = doc(db, 'orders', orderDocIdToUse);
            await updateDoc(orderRef, {
              paymentStatus: 'canceled',
              status: 'canceled',
              updatedAt: serverTimestamp(),
            });
            
            isVerifyingRef.current = false;
            
          } else if (statusId === 4 || statusId === 5) {
            // Payment failed or rejected
            setStatus('failed');
            setMessage(`Payment ${statusResponse.resultObj.status || 'failed'}`);
            
            const orderRef = doc(db, 'orders', orderDocIdToUse);
            await updateDoc(orderRef, {
              paymentStatus: 'failed',
              status: 'pending',
              updatedAt: serverTimestamp(),
            });
            
            isVerifyingRef.current = false;
            
          } else if (statusId === 1 || statusId === 0) {
            // Still pending - check again after delay
            setStatus('pending');
            setMessage('Payment is still processing. Please wait...');
            
            isVerifyingRef.current = false;
            
            // Retry verification after delay
            setTimeout(() => {
              verifyPayment();
            }, 5000);
          }
        } else {
          throw new Error('Invalid payment status response');
        }
      } catch (error) {
        console.error('Error verifying payment status:', error);
        setStatus('failed');
        setMessage('Unable to verify payment. Please try again or contact support.');
        isVerifyingRef.current = false;
      }
    };

    verifyPayment();
  }, [navigate, searchParams, clearCart]);

  const handleRetry = () => {
    isVerifyingRef.current = false;
    window.location.reload();
  };

  return (
    <div className="payment-status-page">
      <div className="payment-status-container">
        {status === 'checking' && (
          <div className="payment-status-content">
            <div className="payment-status-icon checking">
              <div className="spinner"></div>
            </div>
            <h1>Verifying Payment</h1>
            <p>{message}</p>
          </div>
        )}

        {status === 'pending' && (
          <div className="payment-status-content">
            <div className="payment-status-icon pending">
              <div className="spinner"></div>
            </div>
            <h1>Processing Payment</h1>
            <p>{message}</p>
            <p className="payment-status-note">This may take a few moments...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="payment-status-content">
            <div className="payment-status-icon success">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h1>Payment Successful!</h1>
            <p>{message}</p>
            <p className="payment-status-note">Redirecting to receipt...</p>
          </div>
        )}

        {status === 'failed' && (
          <div className="payment-status-content">
            <div className="payment-status-icon failed">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h1>Payment Failed</h1>
            <p>{message}</p>
            <div className="payment-status-actions">
              <button className="btn-primary" onClick={handleRetry}>
                Try Again
              </button>
              <button className="btn-outline" onClick={() => navigate('/shop')}>
                Continue Shopping
              </button>
              <button className="btn-outline" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentStatus;
